import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { applyContent } from '../mock';

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND}/api`;
const TOKEN_KEY = 'cheriyan_admin_token';

const ContentContext = createContext(null);

export function ContentProvider({ children }) {
  const [ready, setReady] = useState(false);
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || '');
  const [contentVersion, setContentVersion] = useState(0);

  const loadContent = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/content`);
      applyContent(data);
    } catch (e) {
      // fall back to defaults from mock.js
      // eslint-disable-next-line no-console
      console.warn('Content API unreachable, using defaults', e?.message);
    } finally {
      setReady(true);
      setContentVersion((v) => v + 1);
    }
  }, []);

  useEffect(() => {
    loadContent();
  }, [loadContent]);

  const login = useCallback(async (email, password) => {
    const { data } = await axios.post(`${API}/auth/login`, { email, password });
    localStorage.setItem(TOKEN_KEY, data.token);
    setToken(data.token);
    return data;
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    setToken('');
  }, []);

  const saveContent = useCallback(
    async (key, dataValue) => {
      const t = localStorage.getItem(TOKEN_KEY);
      await axios.put(
        `${API}/content/${key}`,
        { data: dataValue },
        { headers: { Authorization: `Bearer ${t}` } }
      );
      await loadContent();
    },
    [loadContent]
  );

  const value = {
    ready,
    token,
    isAuthed: !!token,
    login,
    logout,
    saveContent,
    reloadContent: loadContent,
    contentVersion,
    api: API,
  };

  if (!ready) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#546072', fontFamily: 'serif' }}>
        <div style={{ fontSize: 14, letterSpacing: '0.15em', textTransform: 'uppercase' }}>Loading…</div>
      </div>
    );
  }

  return <ContentContext.Provider value={value}>{children}</ContentContext.Provider>;
}

export function useAdmin() {
  const ctx = useContext(ContentContext);
  if (!ctx) throw new Error('useAdmin must be inside ContentProvider');
  return ctx;
}
